<script setup>
  import { unMaskedCreditCardNumber, maskCreditCardNumber, maskExpiredDate, maskAllCharacters } from "@/utils/cards.js";

  import { useCardsStore } from '@/store/stores/cards'
  import { useModalsStore } from '@/store/stores/modals'
  const cardsStore = useCardsStore()
  const modalsStore = useModalsStore()

  import { useRoute, useRouter } from 'vue-router'
  const route = useRoute()
  const router = useRouter()

  import BaseButton from "@/components/base/buttons/BaseButton.vue"
  import Carousel from '@/components/base/swiper/BaseSwiper.vue'
  import TransactionsList from "@/components/transactions/TransactionsList.vue"

  import {defineAsyncComponent, ref, onMounted, computed} from "vue";
  const BaseTextField = defineAsyncComponent (() => import(/* webpackChunkName: "BaseTextField" */ '@/components/base/textField/BaseTextField.vue'));
  const BaseCardSnippet = defineAsyncComponent (() => import(/* webpackChunkName: "BaseSnippet" */ '@/views/cards/CardSnippet/BaseSnippet.vue'));
  const BaseLoader = defineAsyncComponent (() => import(/* webpackChunkName: "components/base/BaseLoader" */ '@/components/base/BaseLoader.vue'));
  const RemoveCardModal = defineAsyncComponent (() => import(/* webpackChunkName: "components/modals/RemoveCardModal" */ '@/components/modals/RemoveCardModal.vue'));
  const FreezeCardModal = defineAsyncComponent (() => import(/* webpackChunkName: "components/modals/FreezeCardModal" */ '@/components/modals/FreezeCardModal.vue'));
  const UnfreezeCardModal = defineAsyncComponent (() => import(/* webpackChunkName: "components/modals/UnfreezeCardModal" */ '@/components/modals/UnfreezeCardModal.vue'));

  let showCardDetails = ref(false)

  const cards = computed(() => {
    return cardsStore.getCards?.filter(({ status }) => status === 'exist')
  })

  const selectedCardIndex = computed(() => {
    const itemId = computed(() => route.params.id);
    const index = cards.value.findIndex(({ id }) => id === itemId.value)
    return  index >= 0 ? index : 0
  })

  function replenishment() {
    router.push({ name: 'ReplenishmentCardPage' })
  }
  function removeCard() {
    cardsStore.removeCard(modalsStore.currentModal.payload.id)
    router.push({ name: 'Cards' })
  }
  function freezeCard() {
    cards.value[selectedCardIndex.value].frozen = true
  }
  function unfreezeCard() {
    cards.value[selectedCardIndex.value].frozen = false
  }

  function changeRouteParam(index) {
    router.replace({ params: { id: cards.value[index].id } });
  }

  onMounted(() => { // <-- Оборачиваем вызов fetchCards в onMounted
    if (!cardsStore.getCards) {
      cardsStore.fetchCards();
    }
  })
</script>
<template>
  <div class="card-main-page">
    <BaseLoader v-if="!cards" class="loader"/>
    <template v-else>
      <Carousel
        :slideWidth="335"
        :gap="20"
        :defaultCardIndex="++selectedCardIndex"
        @updateIndex="changeRouteParam"
      >
        <BaseCardSnippet
          v-for="card in cards"
          :key="card.id"
          :card="card"
          :cards="cards"
          canRemove
        />
      </Carousel>
      <div class="actions">
        <template v-if="!cards[selectedCardIndex].frozen">
          <BaseButton prefix grayscale @click="replenishment">
            <template #label>
              {{ text("card_page.action.replenishment") }}
            </template>
            <template #prefix>
              <svg xmlns="http://www.w3.org/2000/svg" width="25" height="24" viewBox="0 0 25 24" fill="none">
                <path d="M13.8 16.25H11.92C10.59 16.25 9.5 15.13 9.5 13.75C9.5 13.34 9.84 13 10.25 13C10.66 13 11 13.34 11 13.75C11 14.3 11.41 14.75 11.92 14.75H13.8C14.19 14.75 14.5 14.4 14.5 13.97C14.5 13.43 14.35 13.35 14.01 13.23L11 12.18C10.36 11.95 9.5 11.49 9.5 10.02C9.5 8.76999 10.49 7.73999 11.7 7.73999H13.58C14.91 7.73999 16 8.85999 16 10.24C16 10.65 15.66 10.99 15.25 10.99C14.84 10.99 14.5 10.65 14.5 10.24C14.5 9.68999 14.09 9.23999 13.58 9.23999H11.7C11.31 9.23999 11 9.58999 11 10.02C11 10.56 11.15 10.64 11.49 10.76L14.5 11.81C15.14 12.04 16 12.5 16 13.97C16 15.23 15.01 16.25 13.8 16.25Z" fill="white"/>
                <path d="M12.75 17.25C12.34 17.25 12 16.91 12 16.5V7.5C12 7.09 12.34 6.75 12.75 6.75C13.16 6.75 13.5 7.09 13.5 7.5V16.5C13.5 16.91 13.16 17.25 12.75 17.25Z" fill="white"/>
                <path d="M12.75 22.7498C6.82 22.7498 2 17.9298 2 11.9998C2 6.06976 6.82 1.24976 12.75 1.24976C13.16 1.24976 13.5 1.58976 13.5 1.99976C13.5 2.40976 13.16 2.74976 12.75 2.74976C7.65 2.74976 3.5 6.89976 3.5 11.9998C3.5 17.0998 7.65 21.2498 12.75 21.2498C17.85 21.2498 22 17.0998 22 11.9998C22 11.5898 22.34 11.2498 22.75 11.2498C23.16 11.2498 23.5 11.5898 23.5 11.9998C23.5 17.9298 18.68 22.7498 12.75 22.7498Z" fill="white"/>
                <path d="M17.752 2.25C18.162 2.25 18.502 2.59 18.502 3V6.25H21.752C22.162 6.25 22.502 6.59 22.502 7C22.502 7.41 22.162 7.75 21.752 7.75H17.752C17.342 7.75 17.002 7.41 17.002 7V3C17.002 2.59 17.342 2.25 17.752 2.25Z" class="accent-fill" fill="#C7FF24"/>
                <path d="M17.7534 7.74725C17.5634 7.74725 17.3734 7.67725 17.2234 7.52725C16.9334 7.23725 16.9334 6.75726 17.2234 6.46726L22.2234 1.46726C22.5134 1.17726 22.9934 1.17726 23.2834 1.46726C23.5734 1.75726 23.5734 2.23725 23.2834 2.52725L18.2834 7.52725C18.1334 7.67725 17.9434 7.74725 17.7534 7.74725Z" class="accent-fill" fill="#C7FF24"/>
              </svg>
            </template>
          </BaseButton>
          <BaseButton prefix grayscale @click="modalsStore.setModalName('FreezeCardModal')">
            <template #label>
              {{ text("card_page.action.freeze") }}
            </template>
            <template #prefix>
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="20" viewBox="0 0 18 20" fill="none">
                <path d="M6.98524 1.99911L9.1911 4.66611L11.397 1.99911M3.45586 4.0438L4.64533 7.38252L1.25 7.89328M3.45586 15.8675L4.64533 12.6092L1.25 12.018M9.1911 1.11011L9.1911 10.0001M1.54977 14.4451L9.1911 10.0001M1.61248 5.61829L9.1911 10.0001" class="accent-stroke" stroke="#C7FF24" stroke-width="1.5" stroke-linecap="round"/>
                <path d="M15 4.0437L13.8105 7.30194L17.2059 7.89318M15.0433 16.072L13.8105 12.6962L17.2491 12.2225M7.01562 18.001L9.22149 15.334L11.4273 18.001M9.22149 18.89L9.22148 10.0267M16.8628 5.55504L9.22148 10V10.0267M16.9255 14.5082L9.22148 10.0267" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
              </svg>
            </template>
          </BaseButton>
        </template>
        <BaseButton v-else prefix grayscale @click="modalsStore.setModalName('UnfreezeCardModal')">
          <template #label>
            {{ text("card_page.action.unfreeze") }}
          </template>
          <template #prefix>
            <svg xmlns="http://www.w3.org/2000/svg" width="19" height="22" viewBox="0 0 19 22" fill="none">
              <path d="M14.8899 4.66034L15.7702 3.78001M10.4502 2.81734V1.5M10.4502 15.4333V16.7M16.7835 9.10001H18.0502M15.7702 14.42L14.8899 13.5397" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M10.4502 12.9C12.5489 12.9 14.2502 11.1987 14.2502 9.10005C14.2502 7.00137 12.5489 5.30005 10.4502 5.30005" class="accent-stroke" stroke="#C7FF24" stroke-width="1.5" stroke-linecap="round"/>
              <path d="M2.33332 14.6342C1.8233 15.0847 1.46244 15.6798 1.2987 16.3403C1.13496 17.0008 1.17608 17.6955 1.41661 18.3321C1.65713 18.9687 2.08567 19.5171 2.64529 19.9043C3.2049 20.2915 3.86909 20.4993 4.54961 20.5C5.23013 20.5007 5.89476 20.2943 6.45518 19.9083C7.01561 19.5223 7.4453 18.9748 7.68716 18.3387C7.92903 17.7026 7.97161 17.008 7.80926 16.3471C7.64691 15.6863 7.28731 15.0905 6.77823 14.6388M6.78845 14.3529V3.73529C6.78845 2.50078 5.78768 1.5 4.55316 1.5C3.31864 1.5 2.31786 2.50078 2.31786 3.73529V14.3529" class="accent-stroke" stroke="#C7FF24" stroke-width="1.5" stroke-linecap="round"/>
              <path d="M4.58008 17.5V6" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
              <path d="M4.55029 17.3999H4.56029" stroke="white" stroke-width="2.8" stroke-linecap="round"/>
            </svg>
          </template>
        </BaseButton>
      </div>
      <div class="card-details">
        <div class="card-details__title">
          {{ text('card_page.card-details.title') }}

          <div class="btn" @click="showCardDetails = !showCardDetails">
            <template v-if="!showCardDetails">
              {{ text('card_page.action.show') }}
            </template>
            <template v-else>
              {{ text('card_page.action.hide') }}
            </template>
          </div>
        </div>
        <div class="details">
          <BaseTextField>
            <template v-if="showCardDetails">
              {{ unMaskedCreditCardNumber(cards[selectedCardIndex].card_number) }}
            </template>
            <template v-else>
              {{ maskCreditCardNumber(cards[selectedCardIndex].card_number) }}
            </template>
          </BaseTextField>
          <div class="expired-date-and-cvv">
            <BaseTextField>
              <template v-if="showCardDetails">
                {{ cards[selectedCardIndex].exp }}
              </template>
              <template v-else>
                {{ maskExpiredDate(cards[selectedCardIndex].exp) }}
              </template>
            </BaseTextField>
            <BaseTextField>
              <template v-if="showCardDetails">
                {{ cards[selectedCardIndex].cvv }}
              </template>
              <template v-else>
                {{ maskAllCharacters(cards[selectedCardIndex].cvv) }}
              </template>
            </BaseTextField>
          </div>
        </div>
      </div>
      <div class="card-info">
        <div class="card-info__title">
          <span>
            {{ text("card_page.card-info.title") }}
          </span>
          <span v-if="!cards[selectedCardIndex].frozen" class="status active">
            {{ text("card_page.card-info.status.active") }}
          </span>
          <span v-else class="status frozen">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="14" viewBox="0 0 12 14" fill="none">
              <path d="M4.49049 1.66493L5.96106 3.44293L7.43163 1.66493M2.13757 3.02806L2.93055 5.25387L0.666992 5.59438M2.13757 10.9105L2.93055 8.73836L0.666992 8.3442M5.96106 1.07227L5.96106 6.99895M0.866837 9.96228L5.96106 6.99895M0.908646 4.07772L5.96106 6.99895" stroke="white" stroke-linecap="round"/>
              <path d="M9.83366 3.0293L9.04068 5.20146L11.3042 5.59562M9.86251 11.0482L9.04068 8.79762L11.3331 8.48184M4.51074 12.3342L5.98132 10.5562L7.45189 12.3342M5.98132 12.9268L5.98131 7.01797M11.0755 4.03686L5.98131 7.00019V7.01797M11.1173 10.0056L5.98131 7.01797" stroke="#A6A4B4" stroke-linecap="round"/>
            </svg>
            {{ text("card_page.card-info.status.frozen") }}
          </span>
        </div>
        <div class="card-info__row">
          <span class="label">
            {{ text("card_page.rules.min_deposit") }}:
          </span>
          <div class="value">
            {{ cards[selectedCardIndex].rules.min_deposit }} $
          </div>
        </div>
        <div class="card-info__row">
          <span class="label">
            {{ text("card_page.rules.max_deposit") }}:
          </span>
          <div class="value">
            {{ cards[selectedCardIndex].rules.max_deposit }} $
          </div>
        </div>
      </div>
      <div class="transactions-history-wrapper">
        <TransactionsList />
      </div>
    </template>
    <RemoveCardModal @remove="removeCard" />
    <FreezeCardModal @freeze="freezeCard" />
    <UnfreezeCardModal @unfreeze="unfreezeCard" />
  </div>
</template>

<style lang="scss" scoped>
  .card-main-page {
    display: flex;
    flex-direction: column;
    gap: 20px;

    padding: 24px;

    .actions {
      display: flex;
      flex-wrap: nowrap;
      justify-content: space-between;
      gap: 8px;

      svg {
        .accent-stroke {
          stroke: var(--accent-bg);
        }
        .accent-fill {
          fill: var(--accent-bg);
        }
      }
    }
    .card-details {
      display: flex;
      flex-direction: column;
      gap: 16px;

      border-radius: 12px;
      background-color: var(--layout-bg);

      &__title {
        width: 100%;
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;

        font-size: 16px;
        font-weight: 500;

        .btn {
          color: var(--accent-bg);
          font-size: 14px;
          font-weight: 400;

          cursor: pointer;
        }
      }
      .details {
        display: flex;
        flex-wrap: nowrap;
        gap: 8px;

        .expired-date-and-cvv {
          display: flex;
          justify-content: space-between;
          gap: 8px;
          min-width: 50%;
        }
      }
    }
    .card-info {
      display: flex;
      flex-wrap: wrap;
      gap: 16px;

      border-radius: 12px;
      background-color: var(--layout-bg);

      &__title {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: space-between;

        font-size: 16px;
        font-weight: 500;

        .status {
          display: flex;
          align-items: center;
          gap: 7px;

          &.active {
            color: var(--accent-bg);
            background-color: var(--accent-bg-25);
          }

          &.frozen {
            color: var(--text-color-primary);
            background-color: var(--element-bg-lighter);
          }


          padding: 3px 8px;
          border-radius: 14px;

          font-size: 12px;
          font-weight: 400;
        }
      }
      &__row {
        display: flex;
        align-items: center;
        justify-content: space-between;

        flex-grow: 1;

        font-size: 14px;

        .label {
          color: var(--text-color-secondary);
        }
        .value {
          display: flex;
          align-items: center;
          gap: 8px;
        }
      }
    }

    @media (max-width: 920px) {
      .card-details {
        padding: 16px;

        //background-color: var(--layout-bg);
        .details {
          flex-direction: column;
        }
      }
      .card-info {
        flex-direction: column;
        padding: 16px;
      }
    }
    @media (max-width: 576px) {
      padding: 16px;
    }
  }
</style>